import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseCard } from 'src/app/components';
import { AppRoutes } from 'src/app/navigation/routes';
import LocalImages from '../../../static';
import { RxExternalLink } from "react-icons/rx";
import { HiLink } from "react-icons/hi";
import { IoDocumentText } from "react-icons/io5";
import { FaImage } from "react-icons/fa6";
import { IoLogoYoutube } from "react-icons/io5";
import { getFileName, tryCatchWrapper } from 'src/app/utilities/helpers';
import { toast } from 'react-toastify';

type AttachmentCardProps = {
    title: string,
    date: string,
    description: string,
    type: string,
    mimeType: string,
    url: string
};

const AttachmentCard: React.FC<AttachmentCardProps> = ({ title = '', date = '', description = '', type, mimeType, url }) => {

    const navigate = useNavigate();

    const TYPE_URL = 'TYPE_URL';
    const TYPE_ATTACHMENT = 'TYPE_ATTACHMENT';

    const MIME_TYPES_ATTACHMENT = ['application/pdf', 'application/msword', 'text/csv', 'application/vnd.ms-powerpoint'];
    const MIME_TYPE_IMAGE = ['image/png', 'image/jpeg'];
    const MIME_TYPE_URL = 'application/octet-stream';
    const MIME_TYPE_VIDEO = 'video/mp4';

    const downloadFile = async (fileUrl: string, fileName = 'downloaded-file') => {
        await tryCatchWrapper(async () => {
            const response = await fetch(fileUrl, {
                method: 'GET', headers: {
                    'Origin': window.location.origin, // Ensures CORS headers are sent
                }
            }); 
            if (!response?.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const blob = await response?.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();

            window.URL.revokeObjectURL(url); // Free memory
            toast.success('File downloaded successfully.', { autoClose: 5000 });
        });
    };

    const onPressDoc = () => {
        if (!url) return;

        if (type === TYPE_URL) {
            navigate(AppRoutes.RESOURCES_URL, { state: { url: url } });
        }

        if (type === TYPE_ATTACHMENT) {
            navigate(AppRoutes.DOCUMENT_VIEWER, { state: { docs: [{ uri: url }], headerTitle: title}})
        }
    }

    const handleIconByType = () => {
        switch (mimeType) {
            case MIME_TYPES_ATTACHMENT.includes(mimeType) && mimeType:
                return <IoDocumentText size={18} color='#ACACAC' />
            case MIME_TYPE_IMAGE.includes(mimeType) && mimeType:
                return <FaImage size={15} color='#ACACAC' />;
            case MIME_TYPE_URL:
                return <HiLink size={18} color='#ACACAC' />;
            case mimeType.includes('video') && mimeType:
                return <IoLogoYoutube size={16} color='#ACACAC' />;
            default:
                return null;
        }
    }
                
    return (
        <BaseCard clickable={true} customBaseCardStyle='rounded-4 mg-t-15 pd-15'>
            <div className='d-flex'>
                <div className='d-flex flex-grow-1 attachment-card-details' onClick={onPressDoc}>
                    <div className='attachment-card-img'>
                        {handleIconByType()}
                    </div>
                    <div className='mg-x-10 flex-1 d-flex flex-column'>
                        <div className='fs-14 text-black fw-semibold'>{title}</div>
                        <div className='fs-10 text-gray-1000 text-nowrap'>{date}</div>
                    </div>
                </div>
                <div className='attachment-card-icon'>
                    {
                        type === TYPE_ATTACHMENT ? 
                        <img src={LocalImages.downloadIconSvg} alt="" onClick={() => downloadFile(url, getFileName(url as string) as string)}
                        /> : 
                        <RxExternalLink size={20} />
                    }
                </div>
            </div>

            {description && <p className='mb-0 fs-12 text-black mg-t-5'>{description}</p> }
        </BaseCard>
    )
};

export default AttachmentCard;